import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
} from "@mui/material";
import {
  ChevronRight,
  HomeOutlined,
  SchoolOutlined,
  LogoutOutlined,
  SettingsOutlined,
  NotificationsNoneOutlined,
  AccountBoxOutlined,
  BusinessOutlined,
  AssessmentOutlined,
  EmailOutlined,
  PublicOutlined,
  AppRegistrationOutlined,
  WorkspacePremiumOutlined,
  FactCheckOutlined,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../resources/AuthProvider";

import styles from "./Sidebar.module.css";
import Logo from "../../../assets/logo_siscap.svg";
import Icone from "../../../assets/Icone siscap.svg";

import { filterMenu } from "./menu/filterMenu";
import { PageType } from "../../../types/Page";

type SidebarProps = {
  open: boolean;
  pages: PageType[];
  mobile?: boolean;
  onCloseMobile?: () => void;
};

const Sidebar: React.FC<SidebarProps> = ({ open, pages, mobile = false, onCloseMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [submenuTitle, setSubmenuTitle] = useState<string | null>(null);
  const [submenuItems, setSubmenuItems] = useState<any[]>([]);

  const originalMenu = [
    {
      title: "Página Inicial",
      url: "/",
      icon: <HomeOutlined fontSize="medium" />,
    },
    {
      title: "Ações de Qualificação",
      icon: <SchoolOutlined fontSize="medium" />,
      children: [
        { title: "Cadastrar ação", url: "/nova-acao", blank: false },
        { title: "Listar ações", url: "/acoes-qualificacao", blank: false },
        { title: "Realizar inscrição", url: "/realizar-inscricao", blank: false },
        { title: "Gerenciar minhas inscrições", url: "/gerenciar-inscricoes", blank: false },
      ],
    },
    {
      title: "Certificados e Declarações",
      url: "/certificados-declaracoes",
      icon: <WorkspacePremiumOutlined fontSize="medium" />,
    },
    {
      title: "Gestão de Usuários",
      url: "/user-list",
      icon: <AccountBoxOutlined fontSize="medium" />,
    },
    {
      title: "Gestão de Parceiros",
      icon: <BusinessOutlined fontSize="medium" />,
      children: [
        { title: "Instituições Parceiras", url: "/partners", blank: false },
        { title: "Colaboradores Eventuais", url: "/colaboradores", blank: false },
      ],
    },
    {
      title: "Gestão de Certificados/Declarações",
      url: "/modelos-certificados",
      icon: <FactCheckOutlined fontSize="medium" />,
    },
    {
      title: "Gestão de Pesquisas de Satisfação",
      url: "/modelos-pesquisa",
      icon: <AssessmentOutlined fontSize="medium" />,
    },
    {
      title: "Painéis Administrativos",
      icon: <AppRegistrationOutlined fontSize="medium" />,
      children: [
        { title: "Parametrização de Campos", url: "/parametrizacao-campos", blank: false },
        { title: "Gestão de Páginas", url: "/paginas", blank: false },
        { title: "Gestão de Perfis", url: "/perfis", blank: false },
      ],
    },
    {
      title: "Relatórios",
      url: "/relatorios",
      icon: <AssessmentOutlined fontSize="medium" />,
    },
    {
      title: "Mobilização",
      url: "/mobilização",
      icon: <EmailOutlined fontSize="medium" />,
    },
    {
      title: "Links externos",
      icon: <PublicOutlined fontSize="medium" />,
      children: [
        { title: "Plataforma EducaSUAS/MG", url: "https://ead.suas.mg.gov.br", blank: true },
        { title: "Blog do SUAS/MG", url: "https://blog.social.mg.gov.br", blank: true },
        { title: "Site Oficial da SEDESE/MG", url: "https://social.mg.gov.br", blank: true },
      ],
    },
  ];

  const allowedUrls = Array.from(new Set(["/", "/editar-perfil", ...pages.map((page) => page.url)]));
  const menuStructure = filterMenu(originalMenu, allowedUrls);
  const settingsMenuChildren = [
    { title: "Editar meu perfil", url: "/editar-perfil", blank: false, permissionUrl: "/editar-perfil" },
    { title: "Gestão de Módulos", url: "/modulos", blank: false },
    { title: "Gestão de Ações", url: "/acoes-permissao", blank: false },
  ].filter((item) => allowedUrls.includes(item.url) || allowedUrls.includes(item.permissionUrl || ""));

  const hasActiveRouteInChildren = (children: any[]): boolean => {
    return children.some((child) => {
      if (child.url && child.url === location.pathname) return true;
      if (child.children) return hasActiveRouteInChildren(child.children);
      return false;
    });
  };

  const handleSubmenuToggle = (event: React.MouseEvent<HTMLElement>, title: string, children: any[]) => {
    if (submenuTitle === title && submenuAnchor) {
      setSubmenuAnchor(null);
      setSubmenuTitle(null);
      setSubmenuItems([]);
      return;
    }

    setSubmenuAnchor(event.currentTarget);
    setSubmenuTitle(title);
    setSubmenuItems(children);
  };

  const closeSubmenu = () => {
    setSubmenuAnchor(null);
    setSubmenuTitle(null);
    setSubmenuItems([]);
  };

  const handleLogoutClick = () => {
    closeSubmenu();
    logout();
    if (mobile && onCloseMobile) {
      onCloseMobile();
    }
    navigate("/login");
  };

  const drawerWidth = open ? 240 : 70;

  return (
    <Drawer
      variant={mobile ? "temporary" : "permanent"}
      open={open}
      onClose={onCloseMobile}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: "white",
          transition: "width 0.3s ease",
          overflowX: "hidden",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        },
      }}
    >
      <div className={styles.container}>
        <div className={styles.blockTitle}>
          {open ? (
            <img src={Logo} alt="SISCAP" className={styles.logo} />
          ) : (
            <img src={Icone} alt="SISCAP" className={styles.icone} />
          )}
        </div>

        <List component="nav" className={styles.list}>
          {menuStructure.map((item) => (
            <div key={item.title}>
              {item.children ? (
                <Tooltip title={!open ? item.title : ""} placement="right">
                  <ListItemButton
                    onClick={(event) => handleSubmenuToggle(event, item.title, item.children!)}
                    className={hasActiveRouteInChildren(item.children) ? styles.listItemActive : styles.listItem}
                  >
                    <ListItemIcon
                      className={hasActiveRouteInChildren(item.children) ? styles.listItemIconActive : styles.listItemIcon}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          className: hasActiveRouteInChildren(item.children) ? styles.listItemTextActive : styles.listItemText,
                        }}
                      />
                    )}
                    {open && (
                      <ChevronRight
                        fontSize="small"
                        sx={{ color: hasActiveRouteInChildren(item.children) ? "#FFFFFF" : "#5B5C5D" }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              ) : (
                <Tooltip title={!open ? item.title : ""} placement="right">
                  <ListItemButton
                    component={Link}
                    to={item.url!}
                    onClick={() => {
                      closeSubmenu();
                      if (mobile && onCloseMobile) onCloseMobile();
                    }}
                    className={location.pathname === item.url ? styles.listItemActive : styles.listItem}
                  >
                    <ListItemIcon
                      className={location.pathname === item.url ? styles.listItemIconActive : styles.listItemIcon}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          className: location.pathname === item.url ? styles.listItemTextActive : styles.listItemText,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              )}
            </div>
          ))}
        </List>

        <Popover
          open={Boolean(submenuAnchor)}
          anchorEl={submenuAnchor}
          onClose={closeSubmenu}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                mt: -2,
                ml: 2,
                backgroundColor: "#FFFFFF",
                borderRadius: 3,
                border: "1px solid #E9E9E9",
                minWidth: 260,
                p: 2,
                boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
              },
            },
          }}
        >
          <Box>
            <List disablePadding sx={{ display: "grid", gap: 1 }}>
              {submenuItems.map((child) =>
                child.children ? (
                  child.children.map((sub: any) => (
                    <ListItemButton
                      key={`${child.title}-${sub.title}`}
                      component={sub.blank ? "a" : Link}
                      href={sub.blank ? sub.url : undefined}
                      to={!sub.blank ? sub.url : undefined}
                      target={sub.blank ? "_blank" : "_self"}
                      rel={sub.blank ? "noreferrer" : undefined}
                      onClick={() => {
                        closeSubmenu();
                        if (mobile && onCloseMobile) onCloseMobile();
                      }}
                      sx={{
                        borderRadius: 999,
                        backgroundColor: location.pathname === sub.url ? "#5A81FA" : "#F6F6F6",
                        color: location.pathname === sub.url ? "#FFF" : "#5B5C5D",
                        justifyContent: "center",
                        px: 2,
                        py: 0.75,
                        "&:hover": { backgroundColor: location.pathname === sub.url ? "#4A73EE" : "#ECECEC" },
                      }}
                    >
                      <ListItemText
                        primary={sub.title}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: "0.95rem",
                            textAlign: "center",
                            fontWeight: 400,
                            color: location.pathname === sub.url ? "#FFFFFF" : "#5B5C5D",
                          },
                        }}
                      />
                    </ListItemButton>
                  ))
                ) : (
                  <ListItemButton
                    key={child.title}
                    component={child.blank ? "a" : Link}
                    href={child.blank ? child.url : undefined}
                    to={!child.blank ? child.url : undefined}
                    target={child.blank ? "_blank" : "_self"}
                    rel={child.blank ? "noreferrer" : undefined}
                    onClick={() => {
                      closeSubmenu();
                      if (mobile && onCloseMobile) onCloseMobile();
                    }}
                    sx={{
                      borderRadius: 999,
                      backgroundColor: location.pathname === child.url ? "#5A81FA" : "#F6F6F6",
                      color: location.pathname === child.url ? "#FFF" : "#5B5C5D",
                      justifyContent: "center",
                      px: 2,
                      py: 0.75,
                      "&:hover": { backgroundColor: location.pathname === child.url ? "#4A73EE" : "#ECECEC" },
                    }}
                  >
                    <ListItemText
                      primary={child.title}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: "0.95rem",
                          textAlign: "center",
                          fontWeight: 400,
                          color: location.pathname === child.url ? "#FFFFFF" : "#5B5C5D",
                        },
                      }}
                    />
                  </ListItemButton>
                ),
              )}
            </List>
          </Box>
        </Popover>

        <div>
          <List component="nav">
            <ListItemButton
              component={Link}
              to="#"
              onClick={() => {
                closeSubmenu();
                if (mobile && onCloseMobile) onCloseMobile();
              }}
              className={styles.listItem}
            >
              <ListItemIcon className={styles.listItemIcon}>
                <NotificationsNoneOutlined fontSize="medium" />
              </ListItemIcon>
              {open && (
                <ListItemText
                  className={styles.listItemText}
                  primary="Central de Notificações"
                  primaryTypographyProps={{ className: styles.listItemText }}
                />
              )}
            </ListItemButton>

            <Tooltip title={!open ? "Configurações" : ""} placement="right">
              <ListItemButton
                onClick={(event) => handleSubmenuToggle(event, "settings", settingsMenuChildren)}
                className={hasActiveRouteInChildren(settingsMenuChildren) ? styles.listItemActive : styles.listItem}
              >
                <ListItemIcon
                  className={hasActiveRouteInChildren(settingsMenuChildren) ? styles.listItemIconActive : styles.listItemIcon}
                >
                  <SettingsOutlined fontSize="medium" />
                </ListItemIcon>
                {open && (
                  <ListItemText
                    className={styles.listItemText}
                    primary="Configurações"
                    primaryTypographyProps={{
                      className: hasActiveRouteInChildren(settingsMenuChildren) ? styles.listItemTextActive : styles.listItemText,
                    }}
                  />
                )}
                {open && (
                  <ChevronRight
                    fontSize="small"
                    sx={{ color: hasActiveRouteInChildren(settingsMenuChildren) ? "#FFFFFF" : "#5B5C5D" }}
                  />
                )}
              </ListItemButton>
            </Tooltip>

            <ListItemButton
              onClick={handleLogoutClick}
              className={styles.listItem}
            >
              <ListItemIcon className={styles.listItemIcon}>
                <LogoutOutlined fontSize="medium" />
              </ListItemIcon>
              {open && (
                <ListItemText
                  className={styles.listItemText}
                  primary="Sair"
                  primaryTypographyProps={{ className: styles.listItemText }}
                />
              )}
            </ListItemButton>
          </List>
        </div>
      </div>
    </Drawer>
  );
};

export default Sidebar;
